import React, { useMemo } from 'react';
import { Kabelverteilschrank, Abgangsleitung } from '../../types/network';
import { useStressTestStore } from '../../store/stressTestStore';
import { detailStyles, headerColors } from '../../styles/detailStyles';
import { fmt, fmtOpt } from '../../utils/numberFormat';
import { cableDisplayName, cableTypeLabel } from '../../utils/elementDisplayName';
import { ScenarioCouplingsSection } from './ScenarioCouplingsSection';
import { LOADING_COMPLIANCE_LIMIT_PCT, LOADING_WARNING_PCT } from '../../utils/loadingConstants';

interface ScenarioKvsSidebarProps {
  elementId: string;
  onClose?: () => void;
}

/**
 * ScenarioKvsSidebar - Read-only KVS view for stress test scenarios.
 * - Shows connected cables with stress test results (kVAr, loading, current)
 * - NO editing of connections or parameters
 * - NO Lemonbeat measurements
 */
export const ScenarioKvsSidebar: React.FC<ScenarioKvsSidebarProps> = ({ elementId }) => {
  const stressElements = useStressTestStore(state => state.stressElements);
  const stressTestResults = useStressTestStore(state => state.stressTestResults);

  const element = useMemo(() => {
    return stressElements.find(e => e.id === elementId) as Kabelverteilschrank | undefined;
  }, [stressElements, elementId]);

  const allCables = useMemo(() =>
    stressElements.filter((el): el is Abgangsleitung => el.type === 'abgangsleitung'),
    [stressElements]
  );

  // Aggregate connected cables from busbars (same logic as KabelverteilschrankDetails)
  const connectedLines = useMemo(() => {
    if (!element) return [];

    if (element.busbars && element.busbars.length > 0) {
      const cableIds = new Set<string>();
      for (const busbar of element.busbars) {
        if (busbar.connectedLines) {
          for (const cl of busbar.connectedLines) {
            cableIds.add(cl.id);
          }
        }
      }
      return Array.from(cableIds)
        .map(id => allCables.find(cable => cable.id === id))
        .filter((cable): cable is Abgangsleitung => cable !== undefined);
    }

    if (!element.connectedLines) return [];
    return element.connectedLines
      .map(cl => allCables.find(cable => cable.id === cl.id))
      .filter((cable): cable is Abgangsleitung => cable !== undefined);
  }, [element, allCables]);

  // Aggregate totals from connected cables
  const cableSummary = useMemo(() => {
    let totalPowerKw = 0;
    let totalPowerKvar = 0;
    let totalPowerKva = 0;
    let overloadedCount = 0;
    let maxLoading = 0;

    for (const cable of connectedLines) {
      const firstSegWithData = cable.segments?.find(seg => stressTestResults?.results?.[seg.id]);
      const segData = firstSegWithData ? stressTestResults?.results?.[firstSegWithData.id] : undefined;
      if (segData) {
        totalPowerKw += Math.abs(segData.powerKw ?? 0);
        totalPowerKvar += Math.abs(segData.powerKvar ?? 0);
        totalPowerKva += Math.abs(segData.powerKva ?? 0);
        if (segData.isOverloaded) overloadedCount++;
        if ((segData.loadingPercent ?? 0) > maxLoading) maxLoading = segData.loadingPercent ?? 0;
      }
    }

    return { totalPowerKw, totalPowerKvar, totalPowerKva, overloadedCount, maxLoading };
  }, [connectedLines, stressTestResults]);

  if (!element) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-primary-400">
        Kabelverteilschrank nicht gefunden
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${detailStyles.layout.section}`}>
      {/* Header */}
      <div className={`${detailStyles.header.wrapper} ${headerColors.kabelverteilschrank.bg}`}>
        <div className={detailStyles.header.content}>
          <div className="flex items-center space-x-3">
            <div className={detailStyles.header.iconBox}>
              <svg className={`${detailStyles.header.icon} ${headerColors.kabelverteilschrank.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div>
              <h2 className={detailStyles.header.title}>Kabelverteilschrank</h2>
              <p className={detailStyles.header.subtitle}>{element.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-orange-500/80 text-white text-xs font-medium rounded">
              Stresstest
            </span>
          </div>
        </div>
        <div className={`${detailStyles.header.id} ${headerColors.kabelverteilschrank.id}`}>ID: {element.id}</div>
      </div>

      {/* Info Banner */}
      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md px-3 py-2">
        <p className="text-xs text-orange-800 dark:text-orange-200">
          Stresstest-Ansicht - Nur Lesezugriff
        </p>
      </div>

      {/* Summary Cards */}
      {stressTestResults && (
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-gray-50 dark:bg-primary-700 p-3 rounded-lg border border-gray-200 dark:border-primary-600">
            <p className="text-xs text-gray-600 dark:text-primary-300">Kabel</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {connectedLines.length}
            </p>
          </div>
          <div className={`p-3 rounded-lg border ${
            cableSummary.overloadedCount > 0
              ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
              : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
          }`}>
            <p className="text-xs text-gray-600 dark:text-primary-300">Max. Auslastung</p>
            <p className={`text-lg font-semibold ${
              cableSummary.maxLoading >= LOADING_COMPLIANCE_LIMIT_PCT ? 'text-red-600 dark:text-red-400' :
              cableSummary.maxLoading >= LOADING_WARNING_PCT ? 'text-orange-600 dark:text-orange-400' :
              'text-green-600 dark:text-green-400'
            }`}>
              {fmt(cableSummary.maxLoading, 1)}%
            </p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
            <p className="text-xs text-blue-600 dark:text-blue-300">Scheinleistung</p>
            <p className="text-lg font-semibold text-blue-700 dark:text-blue-300">
              {fmt(cableSummary.totalPowerKva, 1)} kVA
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-700">
            <p className="text-xs text-purple-600 dark:text-purple-300">Blindleistung</p>
            <p className="text-lg font-semibold text-purple-700 dark:text-purple-300">
              {fmt(cableSummary.totalPowerKvar, 1)} kVAr
            </p>
          </div>
        </div>
      )}

      {/* Connected Cables Section (read-only) */}
      <div className="bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
        <div className="bg-cyan-50 dark:bg-cyan-900/20 px-4 py-2 border-b border-gray-200 dark:border-primary-600">
          <h3 className="text-sm font-medium text-cyan-900 dark:text-cyan-200">
            Angeschlossene Kabel ({connectedLines.length})
          </h3>
        </div>
        <div className="p-4">
          {connectedLines.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-primary-400 text-center py-2">
              Keine Kabel angeschlossen
            </p>
          ) : (
            <div className="space-y-2">
              {connectedLines.map((cable) => {
                const firstSegWithData = cable.segments?.find(seg => stressTestResults?.results?.[seg.id]);
                const segStressData = firstSegWithData ? stressTestResults?.results?.[firstSegWithData.id] : undefined;
                const isOverloaded = segStressData?.isOverloaded ?? false;
                const loadingPercent = segStressData?.loadingPercent ?? 0;

                return (
                  <div
                    key={cable.id}
                    className={`p-3 rounded-lg border ${
                      isOverloaded
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                        : 'bg-gray-50 dark:bg-primary-700 border-gray-200 dark:border-primary-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          isOverloaded ? 'bg-red-500' : loadingPercent > LOADING_WARNING_PCT ? 'bg-yellow-500' : 'bg-green-500'
                        }`} />
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-gray-900 dark:text-primary-100 truncate block" title={cable.name || cable.id}>
                            {cableDisplayName(cable, id => stressElements.find(e => e.id === id))}
                          </span>
                          <div className="text-xs text-gray-500 dark:text-primary-400">
                            {cableTypeLabel(cable)} · {cable.crossSection} mm²
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-sm font-semibold ${
                          isOverloaded
                            ? 'text-red-600 dark:text-red-400'
                            : loadingPercent > LOADING_WARNING_PCT
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-green-600 dark:text-green-400'
                        }`}>
                          {fmt(loadingPercent, 1)}%
                        </span>
                        <div className="text-[10px] text-gray-500 dark:text-primary-400">Auslastung</div>
                      </div>
                    </div>
                    {segStressData && (
                      <div className="mt-2 space-y-2">
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="text-gray-600 dark:text-primary-400">
                            <span className="font-medium text-gray-900 dark:text-primary-100">
                              {segStressData.powerKva != null
                                ? `${fmt(Math.abs(segStressData.powerKva), 1)} kVA`
                                : `${fmt(Math.abs(segStressData.powerKw ?? 0), 1)} kW`}
                            </span>
                          </div>
                          <div className="text-gray-600 dark:text-primary-400">
                            <span className="font-medium text-purple-700 dark:text-purple-300">
                              {segStressData.powerKvar != null ? fmt(Math.abs(segStressData.powerKvar), 1) : '–'} kVAr
                            </span>
                          </div>
                          <div className="text-gray-600 dark:text-primary-400">
                            <span className="font-medium text-gray-900 dark:text-primary-100">
                              {fmtOpt(segStressData.currentA, 1) ?? '–'} A
                            </span>
                          </div>
                        </div>
                        {/* Per-phase current L1/L2/L3 */}
                        {(segStressData.currentL1A !== undefined || segStressData.currentL2A !== undefined || segStressData.currentL3A !== undefined) && (
                          <div className="grid grid-cols-3 gap-1 text-xs text-center">
                            <div className="bg-white/50 dark:bg-primary-600/50 rounded px-1 py-0.5">
                              <div className="text-[10px] text-gray-500 dark:text-gray-400">L1</div>
                              <div className="font-medium text-gray-900 dark:text-primary-100">
                                {fmtOpt(segStressData.currentL1A, 1) ?? '–'} A
                              </div>
                            </div>
                            <div className="bg-white/50 dark:bg-primary-600/50 rounded px-1 py-0.5">
                              <div className="text-[10px] text-gray-500 dark:text-gray-400">L2</div>
                              <div className="font-medium text-gray-900 dark:text-primary-100">
                                {fmtOpt(segStressData.currentL2A, 1) ?? '–'} A
                              </div>
                            </div>
                            <div className="bg-white/50 dark:bg-primary-600/50 rounded px-1 py-0.5">
                              <div className="text-[10px] text-gray-500 dark:text-gray-400">L3</div>
                              <div className="font-medium text-gray-900 dark:text-primary-100">
                                {fmtOpt(segStressData.currentL3A, 1) ?? '–'} A
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Trennstellen fused in this KVS — the coupling cables are neighbour-zone elements and never
          appear in the cable list above (NETZVERBUND.md 6) */}
      <ScenarioCouplingsSection switchElementId={element.id} />
    </div>
  );
};
